import { useState } from "react";
import { CsvSchema, OcrConfig, SlipTypeConfig } from "../services/csvSettingsService";
import { useCsvColumns } from "../hooks/useCsvColumns";

export function useSlipTypeConfiguration(
  slipTypeConfigs?: Record<string, SlipTypeConfig>,
  onChangeSlipTypeConfigs?: (configs: Record<string, SlipTypeConfig>) => void
) {
  // === 伝票種別 ===
  const slipTypeNames = slipTypeConfigs ? Object.keys(slipTypeConfigs) : [];
  const [selectedSlipType, setSelectedSlipType] = useState<string | null>(
    slipTypeNames.length > 0 ? slipTypeNames[0] : null
  );
  const [addSlipTypeModalOpen, setAddSlipTypeModalOpen] = useState(false);
  const [newSlipTypeName, setNewSlipTypeName] = useState("");

  const hasSlipTypeSupport = !!slipTypeConfigs && !!onChangeSlipTypeConfigs;

  // 選択中の種別のCSV/プロンプト設定
  const currentSlipConfig: SlipTypeConfig | undefined =
    selectedSlipType && slipTypeConfigs ? slipTypeConfigs[selectedSlipType] : undefined;

  // 種別固有のCSV値（未設定時は空のデフォルト）
  const activeCsvSchema = currentSlipConfig?.csvSchema || {
    columns: [],
    encoding: "utf-8" as const,
  };
  const activeOcrConfig: OcrConfig | undefined = currentSlipConfig
    ? { promptAddition: currentSlipConfig.promptAddition }
    : undefined;

  // 種別固有の変更ハンドラ
  const handleActiveCsvChange = (newCsv: CsvSchema) => {
    if (selectedSlipType && slipTypeConfigs && onChangeSlipTypeConfigs) {
      onChangeSlipTypeConfigs({
        ...slipTypeConfigs,
        [selectedSlipType]: {
          ...slipTypeConfigs[selectedSlipType],
          csvSchema: newCsv,
        },
      });
    }
  };

  const handleActiveOcrConfigChange = (config: OcrConfig) => {
    if (selectedSlipType && slipTypeConfigs && onChangeSlipTypeConfigs) {
      onChangeSlipTypeConfigs({
        ...slipTypeConfigs,
        [selectedSlipType]: {
          ...slipTypeConfigs[selectedSlipType],
          promptAddition: config.promptAddition,
        },
      });
    }
  };

  // 種別追加
  const handleAddSlipType = () => {
    const name = newSlipTypeName.trim();
    if (!name || !onChangeSlipTypeConfigs || !slipTypeConfigs) return;
    if (slipTypeConfigs[name]) {
      return; // 重複防止
    }
    onChangeSlipTypeConfigs({
      ...slipTypeConfigs,
      [name]: {},
    });
    setNewSlipTypeName("");
    setAddSlipTypeModalOpen(false);
    setSelectedSlipType(name);
  };

  // 種別削除
  const handleDeleteSlipType = (typeName: string) => {
    if (!confirm(`伝票種別「${typeName}」の個別設定を削除しますか？`)) return;
    if (!onChangeSlipTypeConfigs || !slipTypeConfigs) return;
    const next = { ...slipTypeConfigs };
    delete next[typeName];
    onChangeSlipTypeConfigs(next);
    // 削除後は残りの先頭種別を選択
    const remaining = Object.keys(next);
    setSelectedSlipType(remaining.length > 0 ? remaining[0] : null);
  };

  // 種別タブで使うカラム操作フック（種別固有CSV用）
  const slipTypeCsvColumns = useCsvColumns(activeCsvSchema, handleActiveCsvChange);

  return {
    slipTypeNames,
    selectedSlipType,
    setSelectedSlipType,
    addSlipTypeModalOpen,
    setAddSlipTypeModalOpen,
    newSlipTypeName,
    setNewSlipTypeName,
    hasSlipTypeSupport,
    currentSlipConfig,
    activeCsvSchema,
    activeOcrConfig,
    handleActiveCsvChange,
    handleActiveOcrConfigChange,
    handleAddSlipType,
    handleDeleteSlipType,
    slipTypeCsvColumns,
  };
}
